/**
 * Saca del monitor serie lo que imprime la librería de trazabilidad.
 *
 * El protocolo define que todo lo que no es una trama no es suyo, y la
 * sesión lo pasa al monitor tal cual -- correcto según la especificación, que
 * justamente permite texto libre para debug. El problema es que ProtocoloEDU usa
 * ese permiso: ModuleDetector::begin() imprime un cartel legible con el perfil,
 * el backend y los umbrales, y hay líneas de error con el mismo estilo. Eso no lo
 * escribió el usuario y no tiene nada que hacer en su monitor.
 *
 * Esto es un parche del lado del host, atado al texto que la librería imprime
 * hoy. El arreglo de fondo es de la librería: o manda esos diagnósticos como
 * tramas -- y entonces la sesión los saca sola y podemos mostrarlos en la consola
 * como el resto del protocolo -- o los deja detrás de un flag que el sketch que
 * generamos apague. Mientras tanto, acá.
 *
 * Trabaja por líneas completas, pero una línea a medias se guarda solo mientras
 * pueda terminar siendo de la librería. Lo que ya no puede -- "7", "Temper" --
 * sale enseguida, y el resto de esa línea pasa sin mirarlo: un sketch que hace
 * Serial.print() sin salto de línea nunca manda el \n, y esperarlo era dejar el
 * monitor en blanco para siempre.
 */

// El cartel de arranque, que es un bloque con principio y fin marcados. Se
// suprime por bloque y no línea por línea: sus líneas del medio ("Placa : ...")
// son demasiado genéricas para descartarlas por su cuenta sin riesgo de comerse
// algo que haya escrito el usuario.
const BANNER_OPEN = /^=== ModuleDetector ===\s*$/;
const BANNER_CLOSE = /^=+\s*$/;

// Líneas sueltas de la librería, con marca propia.
const TAGGED_LINE = /^\s*\[ModuleDetector\]/;
const BANNER_CONTINUATION = /^\s*→ Próximamente:/;

// Cómo empiezan las líneas de la librería, para saber si una línea a medias
// todavía puede ser una de ellas.
const NOISE_STARTS = ['=== ModuleDetector ===', '[ModuleDetector]', '→ Próximamente:'];

const mayBeNoise = (partial) => {
  const start = partial.replace(/^\s+/, '');
  return NOISE_STARTS.some((marker) => marker.startsWith(start) || start.startsWith(marker));
};

export const createFirmwareNoiseFilter = () => {
  let pending = '';
  let inBanner = false;
  // La línea en curso ya se empezó a mostrar: lo que falta de ella pasa tal cual.
  let passthrough = false;

  const isNoise = (line) => {
    if (inBanner) {
      // El cierre del cartel se traga a sí mismo y termina el bloque.
      if (BANNER_CLOSE.test(line)) inBanner = false;
      return true;
    }
    if (BANNER_OPEN.test(line)) {
      inBanner = true;
      return true;
    }
    return TAGGED_LINE.test(line) || BANNER_CONTINUATION.test(line);
  };

  return (text) => {
    if (!text) return '';
    let buffer = `${pending}${text}`;
    pending = '';
    let out = '';

    if (passthrough) {
      const end = buffer.indexOf('\n');
      if (end < 0) return buffer;
      out = buffer.slice(0, end + 1);
      buffer = buffer.slice(end + 1);
      passthrough = false;
    }

    const lastBreak = buffer.lastIndexOf('\n');
    out += buffer
      .slice(0, lastBreak + 1)
      .split('\n')
      // split deja un '' final por el \n de cierre; se descarta y se repone al unir.
      .slice(0, -1)
      .filter((line) => !isNoise(line.replace(/\r$/, '')))
      .map((line) => `${line}\n`)
      .join('');

    const rest = buffer.slice(lastBreak + 1);
    if (rest && !inBanner && !mayBeNoise(rest)) {
      passthrough = true;
      out += rest;
    } else {
      pending = rest;
    }
    return out;
  };
};

export default createFirmwareNoiseFilter;
